// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { PrimaryMetric, SummaryCard } from '@/components/summary-card';
import type { CycleTimeMetric } from '@/report/api/types';
import { CycleTimeDistributionBar } from '@/report/components/cycle-time-distribution-bar';
import type { CycleTimeSegment } from '@/report/components/cycle-time-metrics';
import {
  emptyCycleTimeMetric,
  formatCycleTimeDays,
} from '@/report/components/cycle-time-metrics';
import { pluralize } from '@/utilities/pluralize';
import { Alert, Box, Grid, Stack } from '@mui/material';

interface CycleTimeSummaryCardsProps<K extends string> {
  report: Record<K, CycleTimeMetric> | null;
  segments: readonly CycleTimeSegment<K>[];
  singularNoun: string;
}

function getMetric<K extends string>(
  report: Record<K, CycleTimeMetric> | null,
  segment: CycleTimeSegment<K>
): CycleTimeMetric {
  return report?.[segment.key] ?? emptyCycleTimeMetric();
}

function hasCycleTimeMetricRecords<K extends string>(
  report: Record<K, CycleTimeMetric> | null,
  segments: readonly CycleTimeSegment<K>[]
): boolean {
  if (report === null) {
    return false;
  }

  return segments.some((segment) => report[segment.key].count > 0);
}

export function CycleTimeSummaryCards<K extends string>(
  props: CycleTimeSummaryCardsProps<K>
) {
  const isLoading = props.report === null;
  const hasMetricRecords = hasCycleTimeMetricRecords(
    props.report,
    props.segments
  );

  return (
    <Stack spacing={1.5}>
      {props.report !== null && !hasMetricRecords && (
        <Alert severity="info">
          No {props.singularNoun} cycle-time data in this time range
        </Alert>
      )}
      <Box>
        <Grid container spacing={3}>
          {props.segments.map((segment) => {
            const metric = getMetric(props.report, segment);
            return (
              <Grid item xs={12} md={4} key={segment.key}>
                <SummaryCard>
                  <PrimaryMetric
                    label={segment.label}
                    value={
                      isLoading ? '' : formatCycleTimeDays(metric.medianDays)
                    }
                    color={segment.color}
                    tooltip={`${segment.tooltip} (median)`}
                    isLoading={isLoading}
                    caption={
                      isLoading
                        ? undefined
                        : pluralize(metric.count, {
                            singular: props.singularNoun,
                          })
                    }
                  />
                  <CycleTimeDistributionBar
                    metric={metric}
                    label={segment.label}
                    tooltip={segment.tooltip}
                    isLoading={isLoading}
                    singularNoun={props.singularNoun}
                  />
                </SummaryCard>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Stack>
  );
}
